import { Box, Typography, Button, Sheet } from '@mui/joy';
import { Lock, CheckCircle, CircleHalf, ArrowRight, Path, ClipboardText, Stethoscope, PlayCircle, Exam, Cards, FilePdf } from '@phosphor-icons/react';

/**
 * Карта доступа плитками: одна плитка = один раздел платформы, внутри
 * описание и статус. Дисциплины живут чипами внутри плитки «Учебные планы».
 * Плитка с замком открывает UpgradeModal, открытая ведёт в раздел.
 */

const BLUE = '#65A30D';   // цвет статуса «Открыто» (lime)
const NAVY = '#2A75D3';   // цвет иконок
const SKY = { bg: '#E6F4FD', border: '#B9E1F8', text: '#0B6FA8', sub: '#3A93C7' };   // открытые дисциплины
const CORAL = '#B84C31';
const AMBER = '#B7791F';

const TILE_META = {
    'Учебные планы':      { icon: Path,          hue: NAVY, text: '16 дисциплин по дням: чтение, мини-тесты, задачи и тест-гейт между блоками тем.', to: '/plans' },
    'Мини-тесты':         { icon: ClipboardText, hue: NAVY, text: '10 200+ вопросов в формате USMLE с разбором на русском и английском.', to: '/tests' },
    'Клинические задачи': { icon: Stethoscope,   hue: NAVY, text: '3 900+ задач в стиле UWorld: виньетка, пять вариантов, разбор каждого.', to: '/tests' },
    'Видеолекции':        { icon: PlayCircle,    hue: NAVY, text: '130 лекций по 1,5 часа по всем разделам, кроме биостатистики.', href: 'https://apexmeded.ru' },
    'Режим экзамена':     { icon: Exam,          hue: NAVY, text: 'Экран как на настоящем тесте: счётчик, навигация по вопросам, разбор после ответа.', to: '/tests' },
    'Anki':               { icon: Cards,         hue: NAVY, text: 'Колоды по концептам First Aid к каждому дню плана.', to: '/plans' },
    'PDF презентаций':    { icon: FilePdf,       hue: NAVY, text: 'Презентации лекций, чтобы повторять без видео.', to: '/documents' },
};

function StatusLine({ state, note }) {
    if (state === 'locked') {
        return (
            <Typography level="body-sm" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: CORAL, fontWeight: 700 }}>
                <Lock size={15} weight="fill" /> В APEX PRO
            </Typography>
        );
    }
    if (state === 'partial') {
        return (
            <Typography level="body-sm" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: AMBER, fontWeight: 600 }}>
                <CircleHalf size={15} weight="fill" /> Частично{note ? ` · ${note}` : ''}
            </Typography>
        );
    }
    return (
        <Typography level="body-sm" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: BLUE, fontWeight: 600 }}>
            <CheckCircle size={15} weight="fill" /> Открыто{note ? ` · ${note}` : ''}
        </Typography>
    );
}

function Tile({ name, state, note, wide, children, onClick }) {
    const meta = TILE_META[name] || {};
    const Icon = meta.icon || ClipboardText;
    const locked = state === 'locked';
    return (
        <Sheet
            data-testid={`tile-${name}`}
            data-state={state}
            variant="outlined"
            onClick={onClick}
            sx={{
                gridColumn: wide ? { xs: 'auto', md: '1 / -1' } : 'auto',
                p: 2.25, borderRadius: '16px', borderColor: 'divider',
                bgcolor: 'background.surface',
                display: 'flex', flexDirection: 'column', gap: 1.25,
                cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
                opacity: locked ? 0.92 : 1,
                '&:hover': { transform: 'translateY(-1px)', boxShadow: (t) => t.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 6px 18px rgba(0,0,0,0.06)' },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '11px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: `${meta.hue || '#37352F'}1A`, color: locked ? 'text.tertiary' : (meta.hue || '#37352F'),
                }}>
                    <Icon size={22} weight="duotone" />
                </Box>
                <Typography sx={{ flex: 1, fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', color: locked ? 'text.secondary' : 'text.primary' }}>
                    {name}
                </Typography>
                <ArrowRight size={16} color="#9B9A97" />
            </Box>
            <Typography level="body-sm" sx={{ color: 'text.secondary', lineHeight: 1.5, minHeight: { md: 42 } }}>
                {meta.text}
            </Typography>
            {children}
            <StatusLine state={state} note={note} />
        </Sheet>
    );
}

function DisciplineChips({ disciplines, onOpen, onLocked }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 0.5, sm: 0.75 } }}>
            {disciplines.map(d => {
                const started = !d.locked && d.progress !== 'не начата';
                const state = d.locked ? 'locked' : started ? 'started' : 'open';
                const act = (e) => { e.stopPropagation(); d.locked ? onLocked(d) : onOpen(d); };
                return (
                    <Box
                        key={d.id}
                        role="button"
                        tabIndex={0}
                        data-testid={`discipline-${d.id}`}
                        data-state={state}
                        onClick={act}
                        onKeyDown={(e) => { if (e.key === 'Enter') act(e); }}
                        sx={{
                            display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'stretch' }, justifyContent: 'space-between', gap: 0.5, minWidth: 0,
                            px: 1.25, py: 0.875, borderRadius: '10px', cursor: 'pointer', userSelect: 'none',
                            ...(d.locked
                                ? { border: '1px dashed', borderColor: 'divider', color: 'text.tertiary', bgcolor: 'transparent' }
                                : started
                                    ? { bgcolor: SKY.bg, color: SKY.text, border: '1px solid', borderColor: SKY.border }
                                    : { bgcolor: 'background.level1', color: 'text.primary', border: '1px solid', borderColor: 'divider' }),
                            '&:hover': { filter: 'brightness(0.96)' },
                        }}
                    >
                        <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.25, color: 'inherit', flex: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {d.locked && <Lock size={11} weight="fill" style={{ verticalAlign: '-1px', marginRight: 4 }} />}
                            {d.title}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, lineHeight: 1.2, color: d.locked ? 'text.tertiary' : started ? SKY.sub : 'text.tertiary', fontWeight: 500, flexShrink: 0, textAlign: { xs: 'right', sm: 'left' } }}>
                            {d.locked ? 'в APEX PRO' : d.progress}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

export default function AccessMap({ map, onOpenDiscipline, onLocked, onNavigate }) {
    const byName = Object.fromEntries(map.tools.map(t => [t.name, t]));
    const openCount = map.disciplines.filter(d => !d.locked).length;
    const plans = byName['Учебные планы'] || { state: 'open', note: '' };

    const go = (name, state) => () => {
        if (state === 'locked') return onLocked({ name });
        const meta = TILE_META[name] || {};
        if (meta.href) window.open(meta.href, '_blank', 'noopener');
        else if (meta.to) onNavigate(meta.to);
    };

    const order = ['Мини-тесты', 'Клинические задачи', 'Видеолекции', 'Режим экзамена', 'Anki', 'PDF презентаций'];

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.015em' }}>Что вам открыто</Typography>
                {!map.full && (
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        {openCount} из {map.disciplines.length} дисциплин
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                <Tile name="Учебные планы" state={plans.state} note={map.full ? '' : `${openCount} из ${map.disciplines.length} дисциплин`} wide onClick={go('Учебные планы', plans.state)}>
                    {map.disciplines.length > 0 && (
                        <DisciplineChips disciplines={map.disciplines} onOpen={onOpenDiscipline} onLocked={onLocked} />
                    )}
                </Tile>
                {order.map(name => byName[name] && (
                    <Tile key={name} name={name} state={byName[name].state} note={byName[name].note} onClick={go(name, byName[name].state)} />
                ))}
            </Box>

            {map.cta && (
                <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-start' }}>
                    <Button size="lg" onClick={() => onLocked(null)}
                        sx={{ bgcolor: CORAL, color: '#fff', fontWeight: 700, borderRadius: '10px', px: 3, '&:hover': { bgcolor: '#A03E26' } }}>
                        {map.cta.label}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
